import {
  error,
  ok,
  Result,
  flatMap as flatMapR,
} from 'module/helpers/core/result';
import {
  ofNullable,
  map,
  orElse,
  flatMap,
  filter,
  Maybe,
  Just,
  mapTo,
} from 'module/ui/core/maybe';
import { duration } from 'moment';
import { pipe } from 'ramda';

const parseSingle = pipe(
  (x: string | undefined) => x ?? null,
  ofNullable,
  map((x) => parseInt(x, 10)),
  filter((x) => !Number.isNaN(x)),
  orElse(0),
);

const parseHours = pipe(
  (x: string | undefined) => x ?? null,
  ofNullable,
  map((x) => parseFloat(x)),
  filter((x) => !Number.isNaN(x)),
  orElse(0),
);

type ParsedDuration = {
  hours: string;
  minutes: string;
};

const aggregate_parser = (regexes: RegExp[]) => (value: string) =>
  pipe(
    () => ofNullable(regexes.find((x) => x.test(value ?? '')) ?? null),
    map((x) => value.match(x) ?? null),
    flatMap(ofNullable),
    map((x) => x.groups ?? null),
    flatMap(ofNullable),
    map(
      (x) =>
        ({
          hours: x.hours ?? '0',
          minutes: x.minutes ?? '0',
        }) as ParsedDuration,
    ),
  )();

const regexes = [
  /^(?<hours>-?\d+)h\s?(?<minutes>\d+)m$/,
  /^(?<hours>-?\d+)?:(?<minutes>\d+)$/,
  /^(?<hours>-?(\d+(.\d+)?)|(-?.\d+))h?$/,
  /^(?<minutes>-?\d+)m$/,
];

export const durationParser = pipe(
  aggregate_parser(regexes),
  map(({ hours, minutes }) => [hours, minutes]),
  map(([h, m]) => [parseHours(h), parseSingle(m)]),
  map(([h, m]) => duration(h, 'hours').add(duration(m, 'minutes'))),
  map((d) => d.asMinutes()),
  map(Math.floor),
  map(ok),
  orElse<Result<number, 'invalid_pattern'>>(
    error<'invalid_pattern'>('invalid_pattern'),
  ),
);

export type Parser<T, E> = (value: string) => Result<T, E>;

type WithEmptyError<E> = E | 'empty';
export const nonEmpty =
  <T, E>(p: Parser<T, E>): Parser<T, WithEmptyError<E>> =>
  (value) =>
    value === '' ? error<WithEmptyError<E>>('empty') : p(value);

export const withDefaultValue =
  <T>(empty: T) =>
  <E>(p: Parser<T, E>): Parser<T, E> =>
  (value) =>
    value === '' ? ok(empty) : p(value);

type WithError<E, E2> = E | E2;
const withParsedValueError =
  <T, E2>(hasError: (value: T) => Maybe<E2>) =>
  <E>(p: Parser<T, E>): Parser<T, WithError<E, E2>> =>
    pipe(
      p,
      flatMapR((x) =>
        pipe(
          () => hasError(x),
          map((e) => error<WithError<E, E2>>(e)),
          orElse<Result<T, WithError<E, E2>>>(ok(x)),
        )(),
      ),
    );

export const withPredicatedParseError = <T, E2>(
  e: E2,
  predicate: (value: T) => boolean,
): (<E>(p: Parser<T, E>) => Parser<T, WithError<E, E2>>) =>
  withParsedValueError<T, E2>(pipe(Just, filter(predicate), mapTo(e)));

type ParserReturnType<T, E, E2> = (
  p: Parser<T, E>,
) => Parser<T, WithError<E, E2>>;

export const withMaximum = <E>(
  maximum: number,
): ParserReturnType<number, E, 'exceeds_maximum'> =>
  withPredicatedParseError<number, 'exceeds_maximum'>(
    'exceeds_maximum',
    (x) => x > maximum,
  );

export const withMinimum = <E>(
  minimum: number,
): ParserReturnType<number, E, 'less_than_minimum'> =>
  withPredicatedParseError<number, 'less_than_minimum'>(
    'less_than_minimum',
    (x) => x < minimum,
  );

export const withZeroAsError = withPredicatedParseError<number, 'zero'>(
  'zero',
  (x) => x === 0,
);

const withLessThanOneDay = withPredicatedParseError<
  number,
  'greater_than_one_day'
>('greater_than_one_day', (x) => x > duration(1, 'day').asMinutes());

export const withGreaterThanZero = withPredicatedParseError<
  number,
  'less_than_zero'
>('less_than_zero', (x) => x < 0);

// keeping this for test. We can remove this once we modified the parser.test.ts
export const parser = pipe(
  () => durationParser,
  withGreaterThanZero,
  withZeroAsError,
  withLessThanOneDay,
  nonEmpty,
)();
